// src/components/GridToolbar.jsx
import React, { useEffect } from 'react';

export default function GridToolBar({
  isQuickEdit,
  toggleQuickEdit,
  handleSearchChange,
  showRestoreGrid,
  handleRestoreGrid,
  hasActiveSort,
  handleResetSort,
  tagPreset,
  setTagPreset,
  labelsToPrint,
  handleMakeLabels,
  printOrderPromptOpen,
  showPartialSheetLink,
  onOpenPartialSheet,
  startAtSheetOpen,
  startAtValue,
  startAtMax,
  startAtError,
  onStartAtChange,
  onStartAtCancel,
  onStartAtGenerate,
  displayedAskingTotalLabel = '$0.00',
  askingTotalScopeLabel = 'All Sections',
}) {
  const printDisabled =
    printOrderPromptOpen || labelsToPrint === 0;

  const startAtRaw = typeof startAtValue === 'string' ? parseInt(startAtValue, 10) : startAtValue;
  const startAtValid =
    Number.isFinite(startAtRaw) && startAtRaw >= 1 && (!startAtMax || startAtRaw <= startAtMax);

  useEffect(() => {
    if (!startAtSheetOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onStartAtCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [startAtSheetOpen, onStartAtCancel]);
  return (
    <div className="sticky top-12 z-40 mb-2 bg-white supports-[backdrop-filter]:bg-white/80 backdrop-blur border-b border-gray-200 shadow-sm">
      <div className="w-full px-3 sm:px-4 py-1.5 min-h-10 flex flex-col sm:flex-row items-center gap-2 sm:whitespace-nowrap whitespace-normal sm:overflow-x-auto overflow-visible sm:justify-between flex-wrap">
        {/* LEFT SIDE: Controls */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={toggleQuickEdit}
            className="inline-flex items-center h-8 sm:h-9 px-2.5 sm:px-4 rounded-md text-xs sm:text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 shadow-sm flex-shrink-0"
          >
            {isQuickEdit ? 'Restore Grid' : 'Focus Mode'}
          </button>

          <input
            type="text"
            placeholder="Search by title..."
            onChange={handleSearchChange}
            className="inline-flex items-center h-8 sm:h-9 px-2.5 sm:px-4 rounded-md text-xs sm:text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 shadow-sm flex-shrink-0"
          />
        </div>

        <div className="w-full sm:w-auto inline-flex items-center h-8 sm:h-9 px-2.5 sm:px-3 rounded-md text-xs sm:text-sm font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
          Asking Total ({askingTotalScopeLabel}): {displayedAskingTotalLabel}
        </div>

        {/* RIGHT SIDE: Actions */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          {showRestoreGrid && (
            <button
              type="button"
              onClick={handleRestoreGrid}
              className="inline-flex items-center h-8 sm:h-9 px-2.5 sm:px-4 rounded-md text-xs sm:text-sm font-medium bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 shadow-sm flex-shrink-0"
            >
              Restore Grid
            </button>
          )}
          {hasActiveSort && (
            <button
              type="button"
              onClick={handleResetSort}
              className="inline-flex items-center h-8 sm:h-9 px-2.5 sm:px-4 rounded-md text-xs sm:text-sm font-medium bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 shadow-sm flex-shrink-0"
            >
              Restore Default Order
            </button>
          )}

          {/* LABEL PRESET SELECTOR */}
          <div className="relative w-auto max-w-xs">
            <label htmlFor="preset-select" className="sr-only">
              Create Price Tags
            </label>
            <select
              id="preset-select"
              value={tagPreset}
              onChange={(e) => {
                setTagPreset(e.target.value);
              }}
              className="h-8 sm:h-9 text-xs sm:text-sm bg-white text-slate-900 border border-slate-300 rounded-md pl-2.5 sm:pl-3 pr-7 sm:pr-8 appearance-none focus:outline-none focus:ring-2 focus:ring-slate-300 shadow-sm scucci-label-select"
            >
              <option value="" disabled>
                Create Price Tags
              </option>
              <option value="__H1" disabled>
                STANDARD PRINTER
              </option>
              <option value="AVERY_5160_QR">Avery 5160 / 8160 (QR)</option>
              <option value="AVERY_5160">Avery 5160 / 8160</option>
              <option value="AVERY_5167">Avery 5167 / 8167</option>
              <option value="AVERY_94102">Avery 94102 — ¾&quot; squares</option>
              <option value="PLS275">PLS275 — 1&quot; × ½&quot;</option>
              <option value="__H2" disabled>
                PHONE / BLUETOOTH
              </option>
              <option value="PHOMEMO_30x20">Phomemo — 30 × 20</option>
              <option value="PHOMEMO_40x30">Phomemo — 40 × 30</option>
              <option value="__H3" disabled>
                LABEL PRINTERS
              </option>
              <option value="BROTHER_CSV">Brother — CSV</option>
              <option value="DYMO_CSV">DYMO — CSV</option>
              <option value="ZEBRA_CSV">Zebra — CSV</option>
              <option value="ROLLO_CSV">Rollo — CSV</option>
            </select>
            <svg
              viewBox="0 0 16 16"
              width="12"
              height="12"
              aria-hidden="true"
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
            >
              <path
                d="M4 6 L8 10 L12 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* PRINT BUTTON */}
          {tagPreset && (
            <button
              type="button"
              onClick={handleMakeLabels}
              disabled={printDisabled}
              className={`inline-flex items-center h-8 sm:h-9 px-2.5 sm:px-4 rounded-md text-xs sm:text-sm font-medium shadow-sm focus:outline-none focus:ring-2 flex-shrink-0 ${
                printDisabled
                  ? 'bg-slate-200 text-slate-500 border border-slate-300 cursor-not-allowed'
                  : 'bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-500 focus:ring-emerald-300'
              }`}
            >
              {printOrderPromptOpen
                ? 'Choose Print Order'
                : labelsToPrint > 0
                ? `Print ${labelsToPrint.toLocaleString()} Tags`
                : 'Select Inventory'}
            </button>
          )}
          {showPartialSheetLink && (
            <button
              type="button"
              onClick={onOpenPartialSheet}
              disabled={printDisabled}
              className={`inline-flex items-center h-8 sm:h-9 px-2.5 sm:px-3 rounded-md text-xs sm:text-sm font-medium ${
                printDisabled
                  ? 'text-slate-400 cursor-not-allowed'
                  : 'text-slate-600 hover:text-slate-800 hover:underline'
              }`}
            >
              Use partial sheet
            </button>
          )}
        </div>
      </div>
      {startAtSheetOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={onStartAtCancel}
          />
          <div className="fixed left-1/2 bottom-0 z-50 w-[92%] max-w-sm -translate-x-1/2 rounded-t-2xl bg-white p-4 shadow-2xl max-h-[70vh] overflow-y-auto sm:left-auto sm:bottom-auto sm:right-4 sm:top-24 sm:w-80 sm:translate-x-0 sm:rounded-xl sm:max-h-none sm:overflow-visible">
            <div className="text-base font-semibold text-gray-900">Finish a partially used sheet</div>
            <label htmlFor="start-at-input" className="mt-3 block text-sm font-medium text-gray-700">
              Start at label #
            </label>
            <input
              id="start-at-input"
              type="number"
              min="1"
              max={startAtMax || undefined}
              value={startAtValue}
              onChange={(e) => onStartAtChange(e.target.value)}
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
            <div className="mt-2 text-xs text-gray-500">
              Example: If 5 labels were already used, enter 6.
            </div>
            {startAtError && (
              <div className="mt-2 text-xs text-rose-600">{startAtError}</div>
            )}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onStartAtCancel}
                className="h-8 rounded-md border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onStartAtGenerate}
                disabled={!startAtValid}
                className={`h-8 rounded-md border px-3 text-xs font-medium ${
                  startAtValid
                    ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-500'
                    : 'border-gray-300 bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Generate PDF
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
